// CSV column mapping heuristics — maps detected CSV header strings to
// Huddledin patient fields. English + Hebrew keyword sets. Pure data + pure
// functions — no DOM, no module-bridge dependency. (FIELD_LABELS holds i18n
// keys, resolved to display text via T() in flow.js at render time.)

// The mappable Huddledin field names. Order is the order the mapping UI
// dropdown lists them; the name trio (name / first_name / last_name) comes
// first because at least one of them is required.
export const HUDDLEDIN_FIELDS = [
  'name',
  'first_name',
  'last_name',
  'dob',
  'pronouns',
  'parent_name_hint',
  'parent_email_hint',
  'notes',
];

// Subset of HUDDLEDIN_FIELDS that satisfy the "a name is resolvable"
// requirement. The gate in flow.js requires at least one of these to be
// mapped (and each at most once). Exported so the gating logic stays in
// sync with the heuristic configuration here.
export const NAME_SOURCE_FIELDS = ['name', 'first_name', 'last_name'];

// User-facing labels for each field — i18n KEYS, not literal strings.
// The mapping UI resolves these via T() at render time (flow.js) so the
// dropdown follows the active UI language. `name` is suffixed " (full)" to
// disambiguate it from first/last in the dropdown listing.
export const FIELD_LABELS = {
  name: 'csv_field_name',
  first_name: 'csv_field_first',
  last_name: 'csv_field_last',
  dob: 'csv_field_dob',
  pronouns: 'csv_field_pronouns',
  parent_name_hint: 'csv_field_parent_name',
  parent_email_hint: 'csv_field_parent_email',
  notes: 'csv_field_notes',
};

// Header-keyword heuristics (all lowercase, post-normalization). Each entry
// is matched as an EXACT string against the normalized header (trim +
// lowercase + collapse internal whitespace). Cross-field longest-keyword
// wins, so 'parent email' (kw len 12) beats 'parent' (kw len 6) when
// resolving the header "Parent Email".
// Hebrew aliases are appended to each set (additive only — _normalize's
// toLowerCase is a no-op for Hebrew, so the same exact-match path resolves
// Hebrew headers). No algorithm change.
const HEURISTICS = {
  name: ['name', 'full name', 'patient name', 'student name', 'child name',
    'שם', 'שם מלא', 'שם המטופל', 'שם הילד', 'שם התלמיד'],
  first_name: ['first name', 'first', 'given name', 'fname',
    'שם פרטי'],
  last_name: ['last name', 'last', 'surname', 'family name', 'lname',
    'שם משפחה', 'משפחה'],
  dob: ['dob', 'date of birth', 'birthday', 'birth date', 'born',
    'תאריך לידה', 'ת. לידה', 'יום הולדת', 'תאריך הולדת'],
  pronouns: ['pronouns', 'gender', 'sex',
    'כינויי גוף', 'מגדר', 'מין'],
  parent_name_hint: ['parent', 'parent name', 'guardian', 'mother', 'father', 'caregiver',
    'הורה', 'שם הורה', 'אפוטרופוס', 'אם', 'אב', 'אמא', 'אבא'],
  parent_email_hint: ['parent email', 'email', 'contact email', 'guardian email',
    'אימייל', 'דואל', 'מייל', 'אימייל הורה', 'דוא"ל'],
  notes: ['notes', 'comments', 'description',
    'הערות', 'הערה', 'תיאור', 'פירוט'],
};

// Name concatenation contract — consumed by Sub-commit 6/7's row builder,
// NOT acted on in this module:
// • If the mapping resolves `name` (full) for a row, that value wins.
//   first_name / last_name (if also mapped) are IGNORED for that row.
// • If `name` is unmapped, the row builder joins first_name + last_name
//   as a space-separated "First Last", trimming empty parts:
//     - first only → "First"
//     - last only  → "Last"
//     - both       → "First Last"
// • This module's job is just to expose the targets and gating; the
//   actual join happens downstream.

function _normalize(header) {
  return (header || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// Return the best-matching Huddledin field for a single header, or null if
// no keyword matches exactly. "Best" = longest matching keyword across all
// fields. Exact-match (not substring) — "patient first name" returns null
// rather than mis-mapping to name, which is the safer default for unknown
// formats.
function _bestFieldForHeader(header) {
  const norm = _normalize(header);
  if (!norm) return null;
  let bestField = null;
  let bestKwLen = 0;
  for (const field of HUDDLEDIN_FIELDS) {
    const kws = HEURISTICS[field] || [];
    for (const kw of kws) {
      if (norm === kw && kw.length > bestKwLen) {
        bestField = field;
        bestKwLen = kw.length;
      }
    }
  }
  return bestField;
}

// Suggest a mapping for an array of detected CSV headers. Returns an
// object { csvHeader: huddledinField | null }. Each target field is
// assigned at most once — if two headers both match the same field, the
// first wins and later ones are left null for the user to resolve. Spec:
// "first-match-wins; don't map two CSV columns to the same target by
// default."
export function suggestMapping(headers) {
  const result = {};
  const usedTargets = new Set();
  for (const h of headers || []) {
    const candidate = _bestFieldForHeader(h);
    if (candidate && !usedTargets.has(candidate)) {
      result[h] = candidate;
      usedTargets.add(candidate);
    } else {
      result[h] = null;
    }
  }
  return result;
}
